import type { CreateTariffBody } from '@voxler/contracts';
import type { TariffCurrency } from './use-cases';

export type { TariffCurrency } from './use-cases';
export type TariffMode = CreateTariffBody['mode'];

/**
 * A tariff version as the internal API returns it (serialized domain
 * `ModelTariff`, PRD 8.1): decimal strings in `currency` (its catalog entry's),
 * tokens per million, images per image, messages per SMS segment.
 */
export type Tariff = Omit<CreateTariffBody, 'currency'> & {
  id: string;
  modelId: string;
  currency: TariffCurrency;
};

export const TARIFF_MODES: readonly TariffMode[] = ['standard', 'batch'];

export const TARIFF_MODE_LABELS: Record<TariffMode, string> = {
  standard: 'Standard',
  batch: 'Lot (batch)',
};

/** Symbol of a tariff currency, for an input suffix or a price. */
export const CURRENCY_SYMBOLS: Record<TariffCurrency, string> = {
  USD: '$',
  EUR: '€',
};

export const CURRENCY_LABELS: Record<TariffCurrency, string> = {
  USD: 'USD ($), converti en euros au taux du jour',
  EUR: 'EUR (€), sans conversion',
};

/** A tariff price for display: « 0,08 € », « 3,00 $ » (non-breaking space before the symbol). */
export function formatTariffPrice(price: string, currency: TariffCurrency): string {
  return `${price.replace('.', ',')}\u00a0${CURRENCY_SYMBOLS[currency]}`;
}

/** The version in effect at `now` for a mode: the latest one already effective (PRD 8.2). */
export function tariffInEffect(
  tariffs: readonly Tariff[],
  mode: TariffMode,
  now: number,
): Tariff | undefined {
  let best: Tariff | undefined;
  for (const tariff of tariffs) {
    const from = new Date(tariff.effectiveFrom).getTime();
    if (tariff.mode !== mode || from > now) {
      continue;
    }
    if (best === undefined || from > new Date(best.effectiveFrom).getTime()) {
      best = tariff;
    }
  }
  return best;
}
